import { Dialog, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@thedealersconcierge/components';
import { useAtomValue } from 'jotai';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { CheckBoxGroupRules } from '~/__generated__/backend/zeus';
import Header from '~/components/Header';
import Spinner from '~/components/Spinner';
import CircleCheckMarkIcon from '~/components/icons/CircleCheckMarkIcon';
import config from '~/config';
import {
  getAuthHeaders,
  gqlMutationClient,
  gqlQueryClient
} from '~/lib/backend';
import meQuery from '~/queries/meQuery';
import { useNavigate, useParams } from '~/router';
import PdfFiller, {
  formFieldSelector,
  mkFormDataAtom
} from './_components/PdfFilling';

/**
 * Signs the next document in the signing queue
 *
 * @returns
 */
export default function CollectionPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { transactionId, dealershipSlug } = useParams(
    '/dashboard/:dealershipSlug/:transactionId/sign/next'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: meData, refetch: refetchMe } = useQuery(meQuery());
  const [error, setError] = useState<null | string>(null);
  // Scaling properties - these are needed to scale the document, and inputs, in a way sao that we use the entire width of the screen
  // For small screens, in particular, this is important for accessibility.
  const [contentScale, setContentScale] = useState(1);
  const [maxPageWidth, setMaxPageWidth] = useState(0);
  const formRef = useRef<HTMLDivElement>(null);

  const userId = meData?.me?.user?.id;

  const {
    data: ppSubmission,
    isLoading,
    refetch
  } = useQuery({
    enabled: Boolean(userId),
    queryKey: ['documentSigning', transactionId, dealershipSlug],
    queryFn: () =>
      gqlQueryClient({ dealershipSlug })({
        customer: [
          {
            transactionId,
            userId: userId ?? 'Disabled in this case'
          },
          {
            nextDocumentsToSign: [
              {
                first: 1
              },
              {
                totalCount: true,
                edges: {
                  node: {
                    // Submission ID
                    id: true,
                    formFieldSubmissions: [
                      {
                        filter: {
                          signerId: {
                            equals: userId
                          }
                        }
                      },
                      {
                        edges: {
                          node: {
                            field: formFieldSelector
                          }
                        }
                      }
                    ],
                    form: {
                      formOrder: {
                        position: true
                      }
                    }
                  }
                }
              }
            ]
          }
        ]
      })
  });

  const [formDataAtom, setFormData] = useState(mkFormDataAtom());
  const formData = useAtomValue(formDataAtom);

  const firstSubmission =
    ppSubmission?.customer?.nextDocumentsToSign?.edges?.at(0);

  const hasForm = firstSubmission && !isLoading;
  const submissionId = firstSubmission?.node?.id;

  // Bah, TODO: Rewrite this with something that does not use effects and state.
  const [authHeaders, setAuthHeaders] = useState<Record<string, string>>({});
  useEffect(() => {
    void getAuthHeaders().then(setAuthHeaders);
  }, []);

  const formFieldSubmissions =
    firstSubmission?.node?.formFieldSubmissions?.edges?.map((ffs) => ffs.node);

  const groupForSubmissionField = (formFieldId: string) => {
    return formFieldSubmissions?.find(
      (ffs) => ffs?.field?.formFieldId === formFieldId
    )?.field?.checkboxGroup;
  };

  const submissionFieldIdsForCheckboxGroup = (groupId: string) => {
    // We count the ones
    return formFieldSubmissions
      ?.filter((ffs) => ffs?.field?.checkboxGroup?.id === groupId)
      .map((ffs) => ffs?.field?.formFieldId);
  };

  const allSubmitted =
    Object.entries(formData).filter(([ffsId, e]) => {
      if (e.t === 'string') {
        return e.value.length > 0;
      }
      if (e.t === 'file') {
        return !!e.fileId;
      }
      if (e.t === 'checkbox') {
        // TODO: Validate form rules
        const rule = groupForSubmissionField(ffsId);
        if (!rule) {
          // No rule for this field, all is good!
          return true;
        }

        if (!rule.id) {
          throw new Error('Fatal: No rule ID');
        }

        // Exactly one needs to be checked
        if (rule.rule === CheckBoxGroupRules.EXACTLY_N) {
          const submissionIdsIdGroup = submissionFieldIdsForCheckboxGroup(
            rule.id
          );
          if (!submissionIdsIdGroup) {
            throw new Error('Fatal: No group IDs');
          }

          // Count how many of that that are checkboxes and checked
          const count = submissionIdsIdGroup.filter((id) => {
            if (!id) throw new Error('Fatal: No id');
            return formData[id].t === 'checkbox' && formData[id].value;
          }).length;

          // Count is exactly N
          return count === rule.n;
        }

        // Always passed
        return true;
      }
    }).length === (formFieldSubmissions?.length ?? -1);

  const onNext = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const values = Object.keys(formData).map((formFieldId) => {
        const v = formData[formFieldId];

        return {
          submissionFieldId: formFieldId,
          value:
            v.t === 'string' || v.t === 'checkbox' ? `${v.value}` : undefined,
          // For signatures
          fileId: v.t === 'file' ? v.fileId : undefined
        };
      });

      if (!submissionId) {
        throw new Error('No submission ID');
      }

      const resp = await gqlMutationClient({ dealershipSlug })({
        submitFormValues: [
          {
            formSubmissionId: submissionId,
            values: values
          },
          {
            __typename: true,
            '...on GraphQLError': {
              message: true
            },
            '...on MutationSubmitFormValuesSuccess': {
              data: {
                status: true
              }
            }
          }
        ]
      });

      if (
        resp.submitFormValues?.__typename !== 'MutationSubmitFormValuesSuccess'
      ) {
        setError(resp.submitFormValues?.message ?? 'Unexpected error');

        return;
      }

      // Reset potential error
      setError(null);
      setFormData(mkFormDataAtom());

      const [refetchValue] = await Promise.all([
        refetch(),

        // I am not entirely sure why we need to refetch me
        // Please remove if you can verify that this does not affect anything
        refetchMe()
      ]);
      const refetchedSubmission =
        refetchValue.data?.customer?.nextDocumentsToSign?.edges?.at(0);

      // If there is no more submissions, then we redirect in 3 sec
      if (!refetchedSubmission) {
        setTimeout(() => {
          navigate('/dashboard/:dealershipSlug/:transactionId', {
            params: {
              dealershipSlug,
              transactionId
            }
          });
        }, 3_000);
      } else {
        // This is a hack to trigger re-scaling of the new document that is being shown
        // Typically, the pages have the same size, and for some reason, the next page will have a smaller scale
        setMaxPageWidth(0);

        // Scroll container to top so the user see the next document from the first page
        formRef.current?.scrollTo(0, 0);

        // Because there is so much non-standard state loading here, this
        // seems to be the way to ensure that the state is alright for the
        // next form.
        // This reload seems to break something. It seems to be to be "NetworkError: Load failed".
        // window.location.reload();
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, submissionId, refetch]);

  // This is a memo because the PDF file can not have the component re-rendered
  // When this happens the entered data is removed and it gives a poor user experience.
  // This is quite messy, and we are definitely open for better solutions!
  //
  // In development mode this will throw an error because we use strict mode React
  const content = useMemo(() => {
    return (
      <>
        {/* We only draw the form when we have all the information */}
        {submissionId &&
          (firstSubmission?.node?.formFieldSubmissions?.edges?.length ?? 0) >
            0 &&
          authHeaders && (
            <PdfFiller
              dealershipSlug={dealershipSlug}
              pdfUrl={`${config.rawBackendUrl}/compliance/pdf/${submissionId}`}
              authHeaders={authHeaders}
              formDataAtom={formDataAtom}
              formFieldSubmissions={
                firstSubmission.node?.formFieldSubmissions?.edges?.map(
                  (e) => e.node?.field ?? {}
                ) ?? []
              }
              onUpdateMaxPageWidth={(max) => {
                setMaxPageWidth(max);
              }}
            />
          )}
      </>
    );
  }, [
    authHeaders,
    submissionId,
    firstSubmission?.node?.formFieldSubmissions?.edges?.length
  ]);

  // Breakpoint from tailwind config
  const DESKTOP_BREAKPOINT = 1440;

  useEffect(() => {
    const screenWidth = window.innerWidth;

    // We only scale for smaller screens to ensure that we shows as much of the PDF file as possible
    // on the kiosk tables.
    // This could be remade so we only scale when in portrait mode, as that is more natural.
    if (screenWidth >= DESKTOP_BREAKPOINT) {
      setContentScale(1);
      return;
    }

    // Dynamically set the Page Scale into 98% of the page width
    setContentScale((screenWidth * 0.98) / maxPageWidth);

    // Ensure that we scroll to top
  }, [maxPageWidth]);

  return (
    <div className="flex flex-col h-dvh">
      {/* Header */}
      <Header
        title={t('Documents to Sign')}
        leftElement={{
          type: 'CANCEL_AND_BACK_TO_TRANSACTION_MAIN_PAGE',
          transactionId,
          dealershipSlug
        }}
      />

      {/* Body */}
      <div className="flex-1 overflow-hidden relative bg-secondary">
        {/* Inner Scrollable Area */}
        <div className="h-full overflow-y-scroll overscroll-none">
          {/* Loading Spinner */}
          {isLoading && (
            <div
              className="flex w-full aspect-square justify-center items-center relative h-full"
              data-test-id={'signing-ceremony-spinner'}
            >
              <Spinner className="w-10 aspect-square" />
            </div>
          )}

          {/* Content */}
          {!isLoading && (
            <div className="flex justify-center h-full">
              <div
                className="
              flex flex-col w-full space-y-spacing-06 max-h-dvh gap-x-spacing-04
              desktop:gap-x-spacing-05 desktop:px-spacing-06 desktop:max-w-screen-md
            "
              >
                {/* Form content */}
                {error && (
                  <p
                    className="text-body-3 ml-2 rounded-lg py-4 px-6 font-bold text-white bg-negative-primary"
                    data-test-id={'signing-ceremony-submit-error'}
                  >
                    {error}
                  </p>
                )}

                {/* Form Component */}
                {hasForm && (
                  <div
                    ref={formRef}
                    className="flex flex-1 justify-center overflow-y-scroll py-spacing-04"
                    data-test-id="signing-ceremony-content-loaded"
                  >
                    {/* 
                      We have to use css scale since using react-pdf Page width will break our PdfFiller placement 
                      
                      The scaling is applied to a Container and not directly to the Content 
                      this is due to the scaling property can make a 2D element appear bigger or smaller 
                      while maintaining their layout and position in the document flow. 
                      
                      This means if we scale the container the rest will follows automatically. and will not break our PdfFiller placement.

                      More notes & visualization check: https://github.com/TheDealersConcierge/platform/pull/1277
                    */}
                    <div
                      className="relative"
                      style={{
                        transformOrigin: 'top center', // Anchor it to top center so the alignment doesn't change
                        transform: `scale(${contentScale}, ${contentScale})`
                      }}
                    >
                      <div className="relative py-4">{content}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer - Action Buttons */}
      {hasForm && (
        <div className="flex flex-row w-full px-4 py-4 justify-center items-center border-t">
          <div className="flex flex-row desktop:max-w-screen-md  w-full justify-between">
            <Button
              label={t('Cancel')}
              variant="SECONDARY"
              onClick={() => {
                navigate(-1);
              }}
              size="MEDIUM"
              dataTestId="signing-ceremony-cancel-button"
            />

            <Button
              label={t('Next')}
              onClick={() => void onNext()}
              disabled={!allSubmitted}
              size="MEDIUM"
              dataTestId="signing-ceremony-next-button"
            />
          </div>
        </div>
      )}

      {/* Modals - Live outside of the Container since itis absolute */}
      <Transition appear show={!hasForm} as={Fragment}>
        {/* Can not close */}
        <Dialog as="div" className="relative z-30" onClose={() => null}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0">
            <div className="flex min-h-full items-center justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className={
                    'w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all p-6'
                  }
                >
                  <div
                    className="py-4 text-center text-heading-3"
                    data-test-id={'signing-ceremony-success-modal'}
                  >
                    <Dialog.Title className="">
                      Documents Were Signed Successfully!
                    </Dialog.Title>
                  </div>

                  <p className="flex justify-center">
                    <CircleCheckMarkIcon className="text-[#219653] w-8 h-8" />
                  </p>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      {/* Submission Loading Modal */}
      <Transition appear show={isSubmitting} as={Fragment}>
        {/* Can not close */}
        <Dialog as="div" className="relative z-30" onClose={() => null}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className={
                    'w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all p-6'
                  }
                >
                  <div className="py-4 text-center text-heading-3">
                    <Dialog.Title className="">Submitting Form</Dialog.Title>
                  </div>

                  <Spinner className="w-6 aspect-square mx-auto my-4" />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
